import { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Grid,
    Alert,
    CircularProgress,
    Divider,
} from '@mui/material';

function LicenseConfigSection({ config, onChange, onSave, hasChanges, localisation }) {
    const [licenseInfo, setLicenseInfo] = useState(null);
    const [licenseKey, setLicenseKey] = useState('');
    const [saving, setSaving] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const fileInputRef = useRef(null);

    useEffect(() => {
        loadLicense();
    }, []);

    const loadLicense = async () => {
        try {
            const res = await fetch('/api/configuration/license');
            const data = await res.json();
            setLicenseInfo(data.Licensing);
        } catch (err) {
            console.error('Failed to load license info', err);
        }
    };

    const parseDate = (dateStr) => {
        if (!dateStr) return null;
        const [day, month, year] = dateStr.split('/');
        return new Date(`${year}-${month}-${day}`);
    };

    const isExpired = licenseInfo?.ExpiryDate && parseDate(licenseInfo.ExpiryDate) < new Date();

    const applyLicense = async () => {
        if (!licenseKey.trim()) {
            setErrorMsg('Please enter a valid license key');
            return;
        }
        setSaving(true);
        setErrorMsg('');
        setSuccessMsg('');
        const data = new FormData();
        data.append('licenseKey', licenseKey);
        try {
            const res = await fetch('/api/configuration/license', { method: 'POST', body: data });
            if (res.ok && res.status === 200) {
                await res.json();
                localStorage.setItem('licenseActivated', 'true');
                window.location.href = '/site/dashboard';
            } else {
                const errData = await res.json().catch(() => null);
                throw new Error(errData?.message || res.statusText);
            }
        } catch (err) {
            setErrorMsg('License failed to be applied: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    const downloadLicense = async () => {
        if (!licenseKey.trim()) {
            setErrorMsg('Please enter a license key to save');
            return;
        }
        setSaving(true);
        setErrorMsg('');
        try {
            const res = await fetch(`/api/configuration/downloadlicense?licenseKey=${encodeURIComponent(licenseKey)}`);
            const blob = await res.blob();
            const href = window.URL.createObjectURL(blob);
            const a = document.createElement('a');

            a.download = 'fusioninscribe.tkn';

            a.href = href;
            a.click();
            window.URL.revokeObjectURL(href);
        } catch (err) {
            setErrorMsg('Failed to download license: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleFileUpload = async (file) => {
        if (!file) return;
        setSaving(true);
        setErrorMsg('');
        setSuccessMsg('');
        const data = new FormData();
        data.append('file', file);
        try {
            const res = await fetch('/api/configuration/uploadlicense', { method: 'POST', body: data });
            if (res.ok) {
                localStorage.setItem('licenseActivated', 'true');
                window.location.href = '/site/dashboard';
            } else {
                const errText = await res.text();
                throw new Error(errText || 'Upload failed');
            }
        } catch (err) {
            setErrorMsg('License upload failed: ' + err.message);
        } finally {
            setSaving(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    return (
        <Box sx={{ textAlign: 'left' }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 500 }}>
                License Settings
            </Typography>

            {/* Current License Status */}
            {licenseInfo && (
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        Current License
                    </Typography>
                    <Grid container spacing={1}>
                        <Grid item xs={12}>
                            <Typography variant="body2">
                                <strong>Key:</strong> {licenseInfo.LicenseKey || 'N/A'}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2">
                                <strong>Start Date:</strong> {licenseInfo.StartDate || 'N/A'}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography
                                variant="body2"
                                sx={{ color: isExpired ? 'error.main' : 'inherit', fontWeight: isExpired ? 'bold' : 'normal' }}
                            >
                                <strong>Expiry Date:</strong> {licenseInfo.ExpiryDate || 'N/A'}{isExpired ? ' (Expired)' : ''}
                            </Typography>
                        </Grid>
                        {licenseInfo.Modules?.length > 0 && (
                            <Grid item xs={12}>
                                <Typography variant="body2">
                                    <strong>Modules:</strong> {licenseInfo.Modules.map(m => m.Name).join(', ')}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                </Box>
            )}

            <Divider sx={{ mb: 3 }} />

            {/* Apply / Renew License */}
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Apply / Renew License
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Enter your license key and click <strong>Apply</strong> to activate online. If the license server
                cannot be reached, click <strong>Save</strong> to generate a token for manual authentication.
                Alternatively, upload a pre-authenticated license file.
            </Typography>

            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        size="small"
                        label="License Key"
                        value={licenseKey}
                        onChange={(e) => { setLicenseKey(e.target.value); setErrorMsg(''); setSuccessMsg(''); }}
                        autoComplete="off"
                        inputProps={{ autoComplete: 'off', 'data-form-type': 'other' }}
                    />
                </Grid>
                <Grid item>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={applyLicense}
                        disabled={saving || !licenseKey.trim()}
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        Apply
                    </Button>
                </Grid>
                <Grid item>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={downloadLicense}
                        disabled={saving || !licenseKey.trim()}
                    >
                        Save
                    </Button>
                </Grid>
                <Grid item>
                    <Button
                        variant="outlined"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={saving}
                    >
                        Upload License File
                    </Button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".lic,.tkn"
                        style={{ display: 'none' }}
                        onChange={(e) => handleFileUpload(e.target.files?.[0])}
                    />
                </Grid>
            </Grid>

            {errorMsg && (
                <Alert severity="error" sx={{ mt: 2 }}>{errorMsg}</Alert>
            )}
            {successMsg && (
                <Alert severity="success" sx={{ mt: 2 }}>{successMsg}</Alert>
            )}

            <Divider sx={{ my: 3 }} />

            {/* License Server URL */}
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                License Server
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        size="small"
                        label="License Server URL"
                        value={config.url || ''}
                        onChange={(e) => onChange('url', e.target.value)}
                        helperText="URL of the licensing server"
                        autoComplete="off"
                        inputProps={{ autoComplete: 'off', 'data-form-type': 'other' }}
                    />
                </Grid>
            </Grid>

            <Button
                variant="contained"
                color="primary"
                onClick={onSave}
                disabled={!hasChanges}
                sx={{ mt: 3 }}
            >
                {localisation?.["save_button"]?.element_lang || "Save"}
            </Button>
        </Box>
    );
}

export default LicenseConfigSection;
